import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { continueList } from "../lib/continueList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { toast } from "sonner";
import { Plus, Mic, Camera, Link, MoreVertical, Pencil, Trash2, List, ListOrdered, Image as ImageIcon, Loader2 } from "lucide-react";
import VoiceRecordingOverlay from './VoiceRecordingOverlay';
import SortableCategoryItem from './SortableCategoryItem';
import { API } from '../utils/constants';
import recipeCache from '../utils/recipeCache';
import { voiceRecipeErrorMessage } from '../lib/voiceRecipeError';
import { formatImportedIngredients, formatImportedInstructions, withoutSingleStepNumber } from '../utils/quickImportFormatting';
import { matchImportedCategories } from '../utils/importCategories';
import { compressImportImage, photoImportPayload, PHOTO_IMPORT_ERROR, MAX_IMPORT_IMAGES } from '../utils/photoImport';

// `saveFromRecipe`: save-flow v2 — when set, submitting POSTs the form
// payload to /recipes/{saveFromRecipe.id}/save (copy-on-save with edits
// applied at birth) instead of creating/updating one of the user's own
// recipes. `recipe` then carries only the pre-fill values (no id).
const RecipeForm = ({ recipe, onSave, onCancel, adminToken, isEditing, categories = [], onManageCategories, saveFromRecipe = null, submitLabel = null }) => {
  const [formData, setFormData] = useState({
    title: recipe?.title || "",
    categories: recipe?.categories || [],
    creator: recipe?.creator || "",
    source_link: recipe?.source_link || "",
    ingredients: recipe?.ingredients?.join("\n") || "",
    instructions: withoutSingleStepNumber(recipe?.instructions || ""),
    instructions_type: recipe?.instructions_type || "paragraph",
    prep_time: recipe?.prep_time || "",
    cook_time: recipe?.cook_time || "",
    servings: recipe?.servings || "",
    image_url: recipe?.image_url || "",
    notes: recipe?.notes || "",
    is_public: recipe ? recipe.is_public !== false : false
  });
  
  const [categoryInput, setCategoryInput] = useState("");
  const [localCategories, setLocalCategories] = useState(categories);
  const currentCategoriesRef = useRef(categories);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [showVoiceOverlay, setShowVoiceOverlay] = useState(false);
  const formRef = useRef(null);
  const [showImportOverlay, setShowImportOverlay] = useState(false);
  // A synchronous lock also covers permission prompts, image reads and the
  // render before disabled buttons take effect. Dismissing never releases it.
  const quickImportBusyRef = useRef(false);
  const [activeAudioStream, setActiveAudioStream] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(recipe?.image_url || null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const imageFileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const instructionsRef = useRef(null);
  const ingredientsRef = useRef(null);
  const lastIngredientsSelection = useRef({ start: 0, end: 0 });
  const lastInstructionsSelection = useRef({ start: 0, end: 0 });
  
  // Floating toolbar state for Instructions
  const [showFormatToolbar, setShowFormatToolbar] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
  const [selectedText, setSelectedText] = useState({ start: 0, end: 0, text: '' });
  
  // Floating toolbar state for Ingredients
  const [showIngredientsToolbar, setShowIngredientsToolbar] = useState(false);
  const [ingredientsToolbarPosition, setIngredientsToolbarPosition] = useState({ top: 0, left: 0 });
  const [selectedIngredientsText, setSelectedIngredientsText] = useState({ start: 0, end: 0, text: '' });
  
  // Category management states
  const [showManageCategories, setShowManageCategories] = useState(false);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [deleteCategoryDialog, setDeleteCategoryDialog] = useState({ open: false, category: null, recipeCount: 0 });
  
  // Quick Import states
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [isImportingFromLink, setIsImportingFromLink] = useState(false);
  const [isImportingFromPhoto, setIsImportingFromPhoto] = useState(false);
  const [userHasManuallyEdited, setUserHasManuallyEdited] = useState(false);
  const [overwriteConfirm, setOverwriteConfirm] = useState({ open: false, action: null });
  const photoImportInputRef = useRef(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoImportError, setPhotoImportError] = useState('');
  const selectedPhotosRef = useRef([]);
  const photoImportBusyRef = useRef(false);
  const photoImportGenerationRef = useRef(0);

  useEffect(() => () => {
    photoImportGenerationRef.current += 1;
  }, []);

  const updateSelectedPhotos = (photos) => {
    selectedPhotosRef.current = photos;
    setSelectedPhotos(photos);
  };

  const reportPhotoImportError = () => {
    setPhotoImportError(PHOTO_IMPORT_ERROR);
    toast.error(PHOTO_IMPORT_ERROR);
  };

  // Sync local categories when prop changes OR when dialog opens
  useEffect(() => {
    currentCategoriesRef.current = categories;
    setLocalCategories(categories);
  }, [categories]);

  // Removed redundant useEffect - categories come from parent props

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const formHasData = () => {
    return !!(formData.title.trim() || formData.ingredients.trim() || formData.instructions.trim() || formData.creator.trim() || formData.source_link.trim());
  };

  // Update the form's category list AND the localStorage seed cache together
  // so the collection page never loads stale categories after edits made
  // here (even if the form is cancelled afterwards).
  const syncLocalCategories = (next) => {
    currentCategoriesRef.current = next;
    setLocalCategories(next);
    try {
      localStorage.setItem('cachedCategories', JSON.stringify(next));
    } catch { /* storage full/unavailable — cache is best-effort */ }
  };

  // Category management handlers
  const handleCreateCategory = async () => {
    if (!newCategoryInput.trim()) return;
    
    try {
      const response = await axios.post(
        `${API}/categories`,
        { name: newCategoryInput.trim() },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      syncLocalCategories([...localCategories, response.data]);
      setNewCategoryInput('');
      toast.success('Category created!');
    } catch (error) {
      console.error("Failed to create category", error);
      toast.error("Failed to create category");
    }
  };

  const handleUpdateCategory = async (categoryId, newName) => {
    if (!newName.trim()) return;
    
    try {
      await axios.put(
        `${API}/categories/${categoryId}`,
        { name: newName.trim() },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      syncLocalCategories(localCategories.map(cat => 
        cat.id === categoryId ? { ...cat, name: newName.trim() } : cat
      ));
      setEditingCategoryId(null);
      setEditingCategoryName('');
      toast.success('Category updated!');
    } catch (error) {
      console.error("Failed to update category", error);
      toast.error("Failed to update category");
    }
  };

  const handleDeleteCategory = (categoryId) => {
    const category = localCategories.find(cat => cat.id === categoryId);
    if (!category) return;
    
    setDeleteCategoryDialog({
      open: true,
      category: category,
      recipeCount: 0  // You can add recipe count logic if needed
    });
  };

  const confirmDeleteCategory = async () => {
    const { category } = deleteCategoryDialog;
    if (!category) return;
    
    try {
      await axios.delete(
        `${API}/categories/${category.id}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      const updatedCategories = localCategories.filter(cat => cat.id !== category.id);
      syncLocalCategories(updatedCategories);
      
      // Clear the selected category if it matches the deleted one
      if (formData.categories && formData.categories.includes(category.name)) {
        setFormData({ ...formData, categories: formData.categories.filter(c => c !== category.name) });
      }
      
      // Parent component will refresh categories via onManageCategories callback
      
      setDeleteCategoryDialog({ open: false, category: null, recipeCount: 0 });
      toast.success('Category deleted!');
    } catch (error) {
      console.error("Failed to delete category", error);
      toast.error("Failed to delete category");
      setDeleteCategoryDialog({ open: false, category: null, recipeCount: 0 });
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }
    
    const oldIndex = localCategories.findIndex((cat) => cat.id === active.id);
    const newIndex = localCategories.findIndex((cat) => cat.id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    const newCategories = arrayMove(localCategories, oldIndex, newIndex);
    syncLocalCategories(newCategories);
    
    try {
      await axios.put(
        `${API}/categories/reorder`,
        { category_ids: newCategories.map(cat => cat.id) },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('Category order updated!');
    } catch (error) {
      console.error("Failed to update category order", error);
      toast.error("Failed to update category order");
    }
  };

  const startRecordingDirect = async () => {
    if (quickImportBusyRef.current) return;
    quickImportBusyRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      // Determine supported MIME type
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : MediaRecorder.isTypeSupported('audio/mp4') 
          ? 'audio/mp4' 
          : 'audio/wav';
      
      setActiveAudioStream(stream);
      audioStreamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Process the recorded audio
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        await processAudioRecording(audioBlob, mimeType);
      };
      
      mediaRecorder.start(1000);
      setIsRecording(true);
      setShowVoiceOverlay(true);
      setTranscript("");
      
    } catch (error) {
      quickImportBusyRef.current = false;
      console.error('Error starting recording:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Microphone access denied. Please allow microphone access in your browser settings.');
      } else {
        toast.error('Failed to start recording. Please try again.');
      }
    }
  };

  const startRecording = () => {
    if (formHasData()) {
      setOverwriteConfirm({ open: true, action: () => { setUserHasManuallyEdited(false); startRecordingDirect(); } });
      return;
    }
    startRecordingDirect();
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      setIsProcessing(true);
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processAudioRecording = async (audioBlob, mimeType) => {
    setIsProcessing(true);
    
    const timeoutId = setTimeout(() => {
      console.warn("Audio processing timeout - resetting state");
      setIsProcessing(false);
      setShowVoiceOverlay(false);
      quickImportBusyRef.current = false;
      toast.error("Processing timed out. Please try again.");
    }, 60000);
    
    try {
      // Create FormData with audio file
      const formData = new FormData();
      const extension = mimeType.includes('webm') ? 'webm' : mimeType.includes('mp4') ? 'mp4' : 'wav';
      formData.append('file', audioBlob, `recording.${extension}`);
      
      // Step 1: Transcribe audio using Whisper
      setTranscript("Transcribing audio with AI...");
      const transcribeResponse = await axios.post(
        `${API}/transcribe-audio`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${adminToken}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      const transcribedText = transcribeResponse.data.transcript;
      console.log("Whisper transcription:", transcribedText);
      setTranscript(transcribedText);
      
      if (!transcribedText || !transcribedText.trim()) {
        clearTimeout(timeoutId);
        setIsProcessing(false);
        setShowVoiceOverlay(false);
        quickImportBusyRef.current = false;
        toast.error("No speech detected. Please try recording again.");
        return;
      }
      
      // Step 2: Parse the transcript into recipe format
      setTranscript(prev => prev + "\n\nParsing recipe...");
      await processVoiceToRecipe(transcribedText);
      
      clearTimeout(timeoutId);
      
    } catch (error) {
      clearTimeout(timeoutId);
      console.error("Error processing audio:", error);
      setTranscript("");
      setShowVoiceOverlay(false);
      toast.error(error.response?.data?.detail || 'Voice import failed. Please try again.');
      setIsProcessing(false);
      quickImportBusyRef.current = false;
    }
  };

  const processVoiceToRecipe = async (voiceText) => {
    setUserHasManuallyEdited(false);
    setIsProcessing(true);
    console.log("Processing voice text:", voiceText);
    console.log("Admin token:", adminToken ? "Present" : "Missing");
    
    // Safety timeout - reset processing state after 30 seconds if stuck
    const timeoutId = setTimeout(() => {
      console.warn("Voice processing timeout - resetting state");
      setIsProcessing(false);
      setShowVoiceOverlay(false);
      toast.error("Processing timed out. Please try again.");
    }, 30000);
    
    try {
      const response = await axios.post(
        `${API}/voice-to-recipe`,
        { voice_text: voiceText },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      clearTimeout(timeoutId);
      
      
      
      console.log("API response:", response.data);
      const data = response.data;
      setFormData({
        title: data.title || "",
        categories: matchImportedCategories(data.categories, currentCategoriesRef.current),
        creator: data.creator || "",
        source_link: data.source_link || "",
        ingredients: formatImportedIngredients(data.ingredients),
        instructions: formatImportedInstructions(data.instructions),
        instructions_type: "numbered",
        prep_time: data.prep_time || "",
        cook_time: data.cook_time || "",
        servings: data.servings || ""
      });
      
      setTranscript("");
      setShowVoiceOverlay(false);
      toast.success("Recipe parsed successfully! Review and save.");
    } catch (error) {
      clearTimeout(timeoutId);
      console.error("Error processing voice:", error);
      console.error("Error response:", error.response?.data);
      setTranscript("");

      const detail = error.response?.data?.detail;
      toast.error(voiceRecipeErrorMessage(detail));
    } finally {
      setIsProcessing(false);
      setShowVoiceOverlay(false);
      quickImportBusyRef.current = false;
    }
  };

  const handlePhotoImport = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (quickImportBusyRef.current || photoImportBusyRef.current || isProcessing || isImportingFromLink || isRecording || !files.length) return;
    try {
      if (files.length > MAX_IMPORT_IMAGES ||
          files.some(file => !file.type.startsWith('image/'))) {
        throw new Error(PHOTO_IMPORT_ERROR);
      }
      updateSelectedPhotos(files.map(file => ({ file })));
      setPhotoImportError('');
      requestPhotoImport();
    } catch {
      reportPhotoImportError();
    }
  };

  const requestPhotoImport = () => {
    if (quickImportBusyRef.current || photoImportBusyRef.current || !selectedPhotosRef.current.length) return;
    if (formHasData()) {
      setOverwriteConfirm({ open: true, action: processPhotoImport });
      return;
    }
    processPhotoImport();
  };

  const processPhotoImport = async () => {
    if (quickImportBusyRef.current || photoImportBusyRef.current || !selectedPhotosRef.current.length) return;
    quickImportBusyRef.current = true;
    photoImportBusyRef.current = true;
    const generation = ++photoImportGenerationRef.current;
    const photos = [...selectedPhotosRef.current];
    setIsImportingFromPhoto(true);
    setShowImportOverlay(true);
    setPhotoImportError('');
    setTranscript("Processing images...");
    try {
      const images = [];
      // Sequential reads bound peak memory while preserving the selected order.
      for (const photo of photos) {
        images.push(await compressImportImage(photo.file));
        if (generation !== photoImportGenerationRef.current) return;
      }
      const payload = photoImportPayload(images);
      setTranscript("Extracting recipe from images...");
      const response = await axios.post(
        `${API}/image-to-recipe`,
        payload,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      if (generation !== photoImportGenerationRef.current) return;
      const data = response.data;
      if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error(PHOTO_IMPORT_ERROR);
      setUserHasManuallyEdited(false);
      setFormData(previous => ({
        ...previous,
        title: data.title || "",
        categories: matchImportedCategories(data.categories, currentCategoriesRef.current),
        creator: data.creator || "",
        // Screenshot imports must not accept a guessed URL, including from an
        // older backend. Users can add a verified source during review.
        source_link: "",
        ingredients: formatImportedIngredients(data.ingredients),
        instructions: formatImportedInstructions(data.instructions),
        instructions_type: "numbered",
        prep_time: data.prep_time || "",
        cook_time: data.cook_time || "",
        servings: data.servings || ""
      }));
      toast.success("Recipe extracted from images! Review and save.");
    } catch {
      if (generation === photoImportGenerationRef.current) reportPhotoImportError();
    } finally {
      if (generation === photoImportGenerationRef.current) {
        photoImportBusyRef.current = false;
        quickImportBusyRef.current = false;
        setIsImportingFromPhoto(false);
        setShowImportOverlay(false);
        setTranscript("");
      }
    }
  };

  // Link Import (Web Scraping) handler
  const handleLinkImport = async () => {
    if (quickImportBusyRef.current) return;
    if (!linkUrl.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    if (formHasData()) {
      setOverwriteConfirm({ open: true, action: () => { processLinkImport(); } });
      return;
    }
    processLinkImport();
  };

  const processLinkImport = async () => {
    if (quickImportBusyRef.current) return;
    quickImportBusyRef.current = true;
    const importingUrl = linkUrl;
    setUserHasManuallyEdited(false);
    setIsImportingFromLink(true);
    // Close the URL prompt before opening the nested loading modal.
    setShowLinkDialog(false);
    setShowImportOverlay(true);
    setTranscript("Fetching recipe from URL...");

    try {
      const response = await axios.post(
        `${API}/url-to-recipe`,
        { url: importingUrl },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      const data = response.data;
      setFormData({
        title: data.title || "",
        categories: matchImportedCategories(data.categories, currentCategoriesRef.current),
        creator: data.creator || "",
        source_link: data.source_link || importingUrl,
        ingredients: formatImportedIngredients(data.ingredients),
        instructions: formatImportedInstructions(data.instructions),
        instructions_type: "numbered",
        prep_time: data.prep_time || "",
        cook_time: data.cook_time || "",
        servings: data.servings || "",
        image_url: formData.image_url || ""
      });
      
      setTranscript("");
      setShowLinkDialog(false);
      setLinkUrl('');
      toast.success("Recipe imported from URL! Review and save.");
    } catch (error) {
      console.error("Error importing recipe from URL:", error);
      
      // Check if it's an Instagram URL and show a friendlier message
      const isInstagram = importingUrl.includes('instagram.com');
      if (isInstagram) {
        toast.error("Instagram links can't be imported. Try Photo import instead!", {
          duration: 5000
        });
      } else {
        toast.error(error.response?.data?.detail || 'Link import failed. Try Photo import instead.');
      }
      setTranscript("");
    } finally {
      quickImportBusyRef.current = false;
      setIsImportingFromLink(false);
      setShowImportOverlay(false);
    }
  };

  const handleImageUpload = async (e) => {
    console.log("Image upload triggered");
    const file = e.target.files[0];
    if (!file) {
      console.log("No file selected");
      return;
    }

    console.log("File selected:", file.name, file.type, file.size);

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setIsUploadingImage(true);

    // Load image to check dimensions
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (event) => {
      console.log("FileReader loaded");
      img.src = event.target.result;
    };
    
    reader.onerror = (error) => {
      console.error("FileReader error:", error);
      toast.error('Failed to read image file');
      setIsUploadingImage(false);
    };

    img.onload = () => {
      console.log("Image loaded, dimensions:", img.width, "x", img.height);
      const minWidth = 500;
      const minHeight = 400;

      // Check minimum dimensions
      if (img.width < minWidth || img.height < minHeight) {
        toast.error(`Image must be at least ${minWidth}x${minHeight}px. Your image is ${img.width}x${img.height}px.`);
        setIsUploadingImage(false);
        return;
      }

      // Compress and resize image while maintaining aspect ratio
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Calculate dimensions to maintain aspect ratio
      const maxWidth = 1000;
      const maxHeight = 1000;
      
      let newWidth = img.width;
      let newHeight = img.height;
      
      // Scale down if too large, maintaining aspect ratio
      if (img.width > maxWidth || img.height > maxHeight) {
        const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
        newWidth = img.width * ratio;
        newHeight = img.height * ratio;
      }
      
      canvas.width = newWidth;
      canvas.height = newHeight;
      
      // Draw without stretching
      ctx.drawImage(img, 0, 0, newWidth, newHeight);
      
      // Convert to blob with compression
      canvas.toBlob((blob) => {
        console.log("Canvas toBlob completed, blob size:", blob?.size);
        if (!blob) {
          toast.error('Failed to process image (blob creation failed)');
          setIsUploadingImage(false);
          return;
        }
        // Convert blob to base64
        const compressedReader = new FileReader();
        compressedReader.onloadend = () => {
          const base64data = compressedReader.result;
          console.log("Base64 conversion complete, length:", base64data?.length);
          setImagePreview(base64data);
          setFormData(prev => ({ ...prev, image_url: base64data }));
          toast.success('Image uploaded successfully!');
          setIsUploadingImage(false);
        };
        compressedReader.onerror = (error) => {
          console.error("Compressed FileReader error:", error);
          toast.error('Failed to process image (base64 conversion failed)');
          setIsUploadingImage(false);
        };
        compressedReader.readAsDataURL(blob);
      }, 'image/jpeg', 0.85);
    };
    
    img.onerror = (error) => {
      console.error("Image load error:", error);
      toast.error('Failed to load image');
      setIsUploadingImage(false);
    };

    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData({ ...formData, image_url: '' });
    if (imageFileInputRef.current) {
      imageFileInputRef.current.value = '';
    }
  };

  // Handle text selection in instructions field
  const handleInstructionsSelect = (e) => {
    const textarea = e.target;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value.substring(start, end);
    
    if (start !== end) {
      lastInstructionsSelection.current = { start, end };
    }
    
    if (text.trim().length > 0) {
      // Get textarea dimensions
      const textareaWidth = textarea.clientWidth - 24; // account for padding
      const charWidth = 9.2; // approximate character width for this font
      const charsPerLine = Math.floor(textareaWidth / charWidth);
      const lineHeight = 24;
      
      // Calculate wrapped lines up to selection end
      const textUpToEnd = textarea.value.substring(0, end);
      let totalLines = 0;
      let lastLineChars = 0;
      
      // Split by actual newlines first
      const paragraphs = textUpToEnd.split('\n');
      paragraphs.forEach((para) => {
        if (para.length === 0) {
          totalLines += 1;
          lastLineChars = 0;
        } else {
          // Calculate wrapped lines for this paragraph
          const wrappedLines = Math.ceil(para.length / charsPerLine);
          totalLines += wrappedLines;
          // Characters on the last wrapped line
          lastLineChars = para.length % charsPerLine;
          if (lastLineChars === 0 && para.length > 0) {
            lastLineChars = charsPerLine;
          }
        }
      });
      
      // Position at end of last line of selection
      const topOffset = 20 + (totalLines * lineHeight) - textarea.scrollTop + 2;
      const leftOffset = lastLineChars * charWidth;
      
      setToolbarPosition({
        top: topOffset,
        left: leftOffset
      });
      
      setSelectedText({ start, end, text });
      setShowFormatToolbar(true);
    } else {
      setShowFormatToolbar(false);
    }
  };

  // Format selected text as bulleted list (or remove bullets if already bulleted)
  const formatAsBulletedList = (selStart, selEnd) => {
    const textarea = instructionsRef.current;
    if (!textarea) return;
    
    const start = selStart !== undefined ? selStart : selectedText.start;
    const end = selEnd !== undefined ? selEnd : selectedText.end;
    const currentText = formData.instructions;
    const selectedLines = currentText.substring(start, end).split('\n');
    
    // Check if all lines already have bullets
    const allHaveBullets = selectedLines.every(line => {
      const trimmed = line.trim();
      return !trimmed || /^[-•]\s*/.test(trimmed);
    });
    
    let formattedLines;
    if (allHaveBullets) {
      // Remove bullets
      formattedLines = selectedLines.map(line => {
        const trimmed = line.trim();
        if (!trimmed) return '';
        return trimmed.replace(/^[-•]\s*/, '');
      }).join('\n');
    } else {
      // Add bullet points to each line
      formattedLines = selectedLines.map(line => {
        const trimmed = line.trim();
        if (!trimmed) return '';
        // Remove existing bullets/numbers if present
        const cleaned = trimmed.replace(/^[\d]+\.\s*|^[-•]\s*/, '');
        return `• ${cleaned}`;
      }).join('\n');
    }
    
    const newText = currentText.substring(0, start) + formattedLines + currentText.substring(end);
    setFormData({ ...formData, instructions: newText, instructions_type: 'paragraph' });
    
    // Keep toolbar visible and re-select the formatted text
    const newEnd = start + formattedLines.length;
    setSelectedText({ start, end: newEnd, text: formattedLines });
    
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(start, newEnd);
      }
    }, 0);
  };

  // Format selected text as numbered list (or remove numbers if already numbered)
  const formatAsNumberedList = (selStart, selEnd) => {
    const textarea = instructionsRef.current;
    if (!textarea) return;
    
    const start = selStart !== undefined ? selStart : selectedText.start;
    const end = selEnd !== undefined ? selEnd : selectedText.end;
    const currentText = formData.instructions;
    const selectedLines = currentText.substring(start, end).split('\n');
    
    // Check if all lines already have numbers
    const allHaveNumbers = selectedLines.every(line => {
      const trimmed = line.trim();
      return !trimmed || /^[\d]+\.\s*/.test(trimmed);
    });
    
    let formattedLines;
    if (allHaveNumbers) {
      // Remove numbers
      formattedLines = selectedLines.map(line => {
        const trimmed = line.trim();
        if (!trimmed) return '';
        return trimmed.replace(/^[\d]+\.\s*/, '');
      }).join('\n');
    } else {
      // Add numbers to each line
      let num = 1;
      formattedLines = selectedLines.map(line => {
        const trimmed = line.trim();
        if (!trimmed) return '';
        // Remove existing bullets/numbers if present
        const cleaned = trimmed.replace(/^[\d]+\.\s*|^[-•]\s*/, '');
        return `${num++}. ${cleaned}`;
      }).join('\n');
    }
    
    const newText = currentText.substring(0, start) + formattedLines + currentText.substring(end);
    setFormData({ ...formData, instructions: newText, instructions_type: allHaveNumbers ? 'paragraph' : 'numbered' });
    
    // Keep toolbar visible and re-select the formatted text
    const newEnd = start + formattedLines.length;
    setSelectedText({ start, end: newEnd, text: formattedLines });
    
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(start, newEnd);
      }
    }, 0);
  };

  // Hide toolbar when clicking outside
  const handleInstructionsBlur = () => {
    // Delay to allow button clicks to register
    setTimeout(() => {
      setShowFormatToolbar(false);
    }, 200);
  };

  // Ingredients selection handler
  const handleIngredientsSelect = (e) => {
    const textarea = e.target;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value.substring(start, end);
    
    if (start !== end) {
      lastIngredientsSelection.current = { start, end };
    }
    
    if (text.trim().length > 0) {
      // Count newlines to estimate vertical position of END of selection
      const textUpToSelectionEnd = textarea.value.substring(0, end);
      const lines = textUpToSelectionEnd.split('\n').length;
      const lineHeight = 24; // approximate
      
      // Position right below the selection with minimal gap
      const topOffset = 20 + (lines * lineHeight) - textarea.scrollTop + 2;
      
      setIngredientsToolbarPosition({
        top: topOffset,
        left: 0
      });
      
      setSelectedIngredientsText({ start, end, text });
      setShowIngredientsToolbar(true);
    } else {
      setShowIngredientsToolbar(false);
    }
  };

  // Hide ingredients toolbar when clicking outside
  const handleIngredientsBlur = () => {
    setTimeout(() => {
      setShowIngredientsToolbar(false);
    }, 200);
  };

  // Format ingredients as bulleted list (or remove bullets if already bulleted)
  const formatIngredientsBulleted = (selStart, selEnd) => {
    const textarea = ingredientsRef.current;
    if (!textarea) return;
    
    const start = selStart !== undefined ? selStart : selectedIngredientsText.start;
    const end = selEnd !== undefined ? selEnd : selectedIngredientsText.end;
    const currentText = formData.ingredients;
    const selectedLines = currentText.substring(start, end).split('\n');
    
    const isHeader = (line) => {
      const c = line.trim().replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, '');
      return /.*:\s*$/.test(c.trim()) && !/^\d/.test(c.trim());
    };

    const nonHeaderLines = selectedLines.filter(l => l.trim() && !isHeader(l));
    const allHaveBullets = nonHeaderLines.length > 0 && nonHeaderLines.every(line => /^[-•]\s*/.test(line.trim()));
    
    let formattedLines;
    if (allHaveBullets) {
      formattedLines = selectedLines.map(line => {
        const trimmed = line.trim();
        if (!trimmed || isHeader(line)) return trimmed;
        return trimmed.replace(/^[-•]\s*/, '');
      }).join('\n');
    } else {
      formattedLines = selectedLines.map(line => {
        const trimmed = line.trim();
        if (!trimmed || isHeader(line)) return trimmed;
        const cleaned = trimmed.replace(/^[\d]+\.\s*|^[-•]\s*/, '');
        return `• ${cleaned}`;
      }).join('\n');
    }
    
    const newText = currentText.substring(0, start) + formattedLines + currentText.substring(end);
    setFormData({ ...formData, ingredients: newText });
    
    const newEnd = start + formattedLines.length;
    setSelectedIngredientsText({ start, end: newEnd, text: formattedLines });
    
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(start, newEnd);
      }
    }, 0);
  };

  // Format ingredients as numbered list (or remove numbers if already numbered)
  const formatIngredientsNumbered = (selStart, selEnd) => {
    const textarea = ingredientsRef.current;
    if (!textarea) return;
    
    const start = selStart !== undefined ? selStart : selectedIngredientsText.start;
    const end = selEnd !== undefined ? selEnd : selectedIngredientsText.end;
    const currentText = formData.ingredients;
    const selectedLines = currentText.substring(start, end).split('\n');
    
    // Check if all lines already have numbers
    const allHaveNumbers = selectedLines.every(line => {
      const trimmed = line.trim();
      return !trimmed || /^[\d]+\.\s*/.test(trimmed);
    });
    
    let formattedLines;
    if (allHaveNumbers) {
      // Remove numbers
      formattedLines = selectedLines.map(line => {
        const trimmed = line.trim();
        if (!trimmed) return '';
        return trimmed.replace(/^[\d]+\.\s*/, '');
      }).join('\n');
    } else {
      // Add numbers
      let num = 1;
      formattedLines = selectedLines.map(line => {
        const trimmed = line.trim();
        if (!trimmed) return '';
        const cleaned = trimmed.replace(/^[\d]+\.\s*|^[-•]\s*/, '');
        return `${num++}. ${cleaned}`;
      }).join('\n');
    }
    
    const newText = currentText.substring(0, start) + formattedLines + currentText.substring(end);
    setFormData({ ...formData, ingredients: newText });
    
    const newEnd = start + formattedLines.length;
    setSelectedIngredientsText({ start, end: newEnd, text: formattedLines });
    
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(start, newEnd);
      }
    }, 0);
  };

  const handleListEnter = (e, field) => {
    if (e.key !== "Enter" || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey ||
        e.nativeEvent.isComposing || e.keyCode === 229) return;
    const textarea = e.currentTarget;
    const result = continueList(textarea.value, textarea.selectionStart, textarea.selectionEnd);
    if (!result) return;
    e.preventDefault();
    setFormData(previous => ({ ...previous, [field]: result.value }));
    setUserHasManuallyEdited(true);
    requestAnimationFrame(() => {
      if (textarea.isConnected) textarea.setSelectionRange(result.caret, result.caret);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (photoImportBusyRef.current) return;
    
    // Prevent double submission
    if (isSubmitting) return;
    
    console.log("Form submit triggered");
    console.log("formData:", formData);
    console.log("adminToken:", adminToken ? "present" : "missing");
    
    if (!formData.categories || formData.categories.length === 0) {
      toast.error("At least one category is required");
      console.log("Validation failed: no category");
      return;
    }

    // Categories may have changed while this form/import was open. Do not send
    // a stale or invented label and wait for the server to reject the recipe.
    if (formData.categories.some(name => !currentCategoriesRef.current.some(category => category.name === name))) {
      toast.error("Your categories have changed. Remove unavailable categories and choose from your current list.");
      return;
    }
    
    if (!formData.title?.trim()) {
      toast.error("Title is required");
      console.log("Validation failed: no title");
      return;
    }
    
    const ingredientsRaw = formData.ingredients.split("\n");
    const ingredientsList = [];
    for (let i = 0; i < ingredientsRaw.length; i++) {
      const line = ingredientsRaw[i];
      const trimmed = line.trim();
      if (!trimmed) {
        if (ingredientsList.length > 0 && ingredientsList[ingredientsList.length - 1] !== "") {
          ingredientsList.push("");
        }
        continue;
      }
      ingredientsList.push(trimmed);
    }
    while (ingredientsList.length > 0 && ingredientsList[ingredientsList.length - 1] === "") {
      ingredientsList.pop();
    }
    if (ingredientsList.filter(i => i).length === 0) {
      toast.error("At least one ingredient is required");
      console.log("Validation failed: no ingredients");
      return;
    }
    
    if (!formData.instructions?.trim()) {
      toast.error("Instructions are required");
      console.log("Validation failed: no instructions");
      return;
    }
    
    const payload = {
      ...formData,
      ingredients: ingredientsList
    };
    
    console.log("Sending payload:", payload);
    
    setIsSubmitting(true);

    try {
      let savedRecipe = null;
      if (saveFromRecipe) {
        console.log("Saving someone else's recipe:", saveFromRecipe.id);
        const response = await axios.post(`${API}/recipes/${saveFromRecipe.id}/save`, payload, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        savedRecipe = response.data;
        recipeCache.invalidateLists();
        recipeCache.set(savedRecipe);
        toast.success("Saved to your Wellby!");
      } else if (recipe && recipe.id) {
        console.log("Updating recipe:", recipe.id);
        const response = await axios.put(`${API}/recipes/${recipe.id}`, payload, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        savedRecipe = response.data;
        // Invalidate cache for this recipe and all lists
        recipeCache.invalidate(recipe.id);
        toast.success("Recipe updated successfully!");
      } else {
        console.log("Creating new recipe");
        const response = await axios.post(`${API}/recipes`, payload, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        savedRecipe = response.data;
        // Invalidate all lists since we added a new recipe
        recipeCache.invalidateLists();
        // Seed the cache so opening the new recipe right away never depends
        // on the DB read catching up with the write (avoids "no recipe found")
        recipeCache.set(savedRecipe);
        toast.success("Recipe created successfully!");
      }
      // Pass the saved recipe up so the list can update instantly without
      // waiting on a refetch (the DB read can lag right after a write).
      onSave(savedRecipe);
    } catch (error) {
      console.error("Save error:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to save recipe");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVoiceOverlayClose = () => {
    if (isRecording) {
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
      setIsRecording(false);
    }
    setShowVoiceOverlay(false);
    setIsProcessing(false);
  };

  return (
    <>
    <VoiceRecordingOverlay
      isOpen={showVoiceOverlay || showImportOverlay}
      isRecording={isRecording}
      isProcessing={isProcessing || isImportingFromLink || isImportingFromPhoto}
      onStop={stopRecording}
      onClose={showImportOverlay ? () => setShowImportOverlay(false) : handleVoiceOverlayClose}
      audioStream={activeAudioStream}
      formRef={formRef}
    />
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4" data-testid="recipe-form">
      <fieldset disabled={isImportingFromPhoto || isImportingFromLink} className="min-w-0 space-y-4 border-0 p-0 m-0">
      {/* Header with Title and Public/Private Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" style={{ marginTop: '24px', marginBottom: '16px', gap: '12px' }}>
        {/* Save flow: no heading — the "Saving to your Wellby" line above the
            card already sets the context. */}
        {!saveFromRecipe && (
          <h2 style={{ fontSize: '36px', fontFamily: "'Instrument Serif', serif", color: '#51120A', margin: 0, lineHeight: 0.95 }}>
            {isEditing ? "Edit Recipe" : "Create New Recipe"}
          </h2>
        )}
        
        {/* Public/Private Toggle */}
        <div 
          className="flex items-center rounded-full p-1 self-start sm:self-auto"
          style={{ backgroundColor: '#D6D0C2' }}
        >
          <button
            type="button"
            onClick={() => setFormData({ ...formData, is_public: false })}
            className="px-3 py-1 rounded-full text-sm transition-colors"
            style={{ 
              backgroundColor: !formData.is_public ? '#51120A' : 'transparent',
              color: !formData.is_public ? 'white' : '#51120A',
              fontFamily: "'Bricolage Grotesque', sans-serif"
            }}
          >
            Private
          </button>
          <button
            type="button"
            onClick={() => setFormData({ ...formData, is_public: true })}
            className="px-3 py-1 rounded-full text-sm transition-colors"
            style={{ 
              backgroundColor: formData.is_public ? '#51120A' : 'transparent',
              color: formData.is_public ? 'white' : '#51120A',
              fontFamily: "'Bricolage Grotesque', sans-serif"
            }}
          >
            Public
          </button>
        </div>
      </div>
      
      {/* Quick Import Section - Only when creating a brand-new recipe
          (hidden in the save-someone-else's-recipe flow) */}
      {!isEditing && !saveFromRecipe && (
        <div className="mb-6">
          <p className="text-sm mb-3" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", opacity: 0.8 }}>
            Quick Import
          </p>
          <div className="flex flex-col md:flex-row gap-2">
            {/* Voice Button */}
            {!isRecording ? (
              <Button
                type="button"
                onClick={startRecording}
                disabled={isProcessing || isImportingFromPhoto || isImportingFromLink}
                data-testid="voice-input-btn"
                variant="outline"
                className="flex items-center justify-center gap-2 bg-transparent hover-blush transition-colors focus:outline-none focus:ring-0 flex-1"
                style={{ 
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  borderColor: '#51120A',
                  borderWidth: '1px',
                  color: '#51120A'
                }}
              >
                <Mic className="h-4 w-4" />
                Voice
              </Button>
            ) : (
              <Button
                type="button"
                onClick={stopRecording}
                data-testid="voice-stop-btn"
                className="flex items-center justify-center gap-2 animate-pulse focus:outline-none focus:ring-0 flex-1"
                style={{ backgroundColor: '#51120A', borderColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              >
                <Mic className="h-4 w-4" />
                Stop
              </Button>
            )}
            
            {/* Photo Button */}
            <Button
              type="button"
              onClick={() => photoImportInputRef.current?.click()}
              disabled={isProcessing || isImportingFromPhoto || isImportingFromLink || isRecording}
              data-testid="photo-import-btn"
              variant="outline"
              className="flex items-center justify-center gap-2 bg-transparent hover-blush transition-colors focus:outline-none focus:ring-0 flex-1"
              style={{ 
                fontFamily: "'Bricolage Grotesque', sans-serif",
                borderColor: '#51120A',
                borderWidth: '1px',
                color: '#51120A'
              }}
            >
              {isImportingFromPhoto ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ImageIcon className="h-4 w-4" />
              )}
              Photo
            </Button>
            <input
              ref={photoImportInputRef}
              type="file"
              accept="image/*"
              multiple
              aria-label="Recipe screenshots"
              disabled={isProcessing || isImportingFromPhoto || isImportingFromLink || isRecording}
              onChange={handlePhotoImport}
              className="hidden"
            />
            
            {/* Link Button */}
            <Button
              type="button"
              onClick={() => setShowLinkDialog(true)}
              disabled={isProcessing || isImportingFromPhoto || isImportingFromLink || isRecording}
              data-testid="link-import-btn"
              variant="outline"
              className="flex items-center justify-center gap-2 bg-transparent hover-blush transition-colors focus:outline-none focus:ring-0 flex-1"
              style={{ 
                fontFamily: "'Bricolage Grotesque', sans-serif",
                borderColor: '#51120A',
                borderWidth: '1px',
                color: '#51120A'
              }}
            >
              {isImportingFromLink ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Link className="h-4 w-4" />
              )}
              Link
            </Button>
          </div>
          {photoImportError && <p role="alert" className="text-sm mt-3" style={{ color: '#51120A' }}>{photoImportError}</p>}
          {photoImportError && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedPhotos.length > 0 && <Button type="button" variant="outline" disabled={isImportingFromPhoto || isProcessing || isImportingFromLink || isRecording} onClick={requestPhotoImport}>Try again</Button>}
              <Button type="button" variant="outline" disabled={isImportingFromPhoto || isProcessing || isImportingFromLink || isRecording} onClick={() => photoImportInputRef.current?.click()}>Choose different images</Button>
            </div>
          )}

          {/* Divider */}
          <div className="flex items-center gap-4 mt-4">
            <div className="flex-1 h-px" style={{ backgroundColor: '#D6D0C2' }}></div>
            <span className="text-sm" style={{ color: '#A0988A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              or type manually below
            </span>
            <div className="flex-1 h-px" style={{ backgroundColor: '#D6D0C2' }}></div>
          </div>
        </div>
      )}
      
      {/* Processing Status */}
      {transcript && (
        <div className="p-3 rounded-md text-sm" style={{ backgroundColor: '#EEE7D8', border: '1px solid #D6D0C2' }}>
          <p className="font-semibold mb-1" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Status:</p>
          <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>{transcript}</p>
        </div>
      )}

      {/* Link Import Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="sm:max-w-[500px]" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif", fontSize: '28px', lineHeight: 1.1 }}>
              Import from Link
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="mb-2 block" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Recipe URL
              </Label>
              <Input
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com/recipe"
                disabled={isImportingFromLink}
                style={{ 
                  backgroundColor: '#EEE7D8',
                  borderColor: '#D6D0C2',
                  color: '#51120A',
                  fontFamily: "'Bricolage Grotesque', sans-serif"
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleLinkImport();
                  }
                }}
              />
              <p className="text-xs mt-2" style={{ color: '#A0988A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Paste a link to any recipe page and we'll extract the recipe for you
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowLinkDialog(false);
                setLinkUrl('');
              }}
              disabled={isImportingFromLink}
              style={{ 
                borderWidth: '1px',
                borderColor: '#51120A', 
                color: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif"
              }}
              className="hover-blush transition-colors"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleLinkImport}
              disabled={isImportingFromLink || !linkUrl.trim()}
              style={{ backgroundColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
            >
              {isImportingFromLink ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Importing...
                </>
              ) : (
                'Import Recipe'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={overwriteConfirm.open} onOpenChange={(open) => { if (!open) setOverwriteConfirm({ open: false, action: null }); }}>
        <DialogContent className="sm:max-w-[400px]" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif", fontSize: '24px' }}>
              Overwrite manual entry?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm py-2" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            You've already started filling in this recipe. Importing will replace what you've entered.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOverwriteConfirm({ open: false, action: null })}
              style={{ borderColor: '#D6D0C2', color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                const action = overwriteConfirm.action;
                setOverwriteConfirm({ open: false, action: null });
                if (action) action();
              }}
              style={{ backgroundColor: '#51120A', color: '#EEE7D8', fontFamily: "'Bricolage Grotesque', sans-serif" }}
            >
              Yes, overwrite
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Upload Section */}
      <div>
        <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Recipe Photo</Label>
        <div className="mt-2">
          {imagePreview ? (
            <div className="relative w-full" style={{ height: '200px' }}>
              <img 
                src={imagePreview} 
                alt="Recipe preview" 
                className="rounded-lg"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                  objectPosition: 'center',
                  border: '1px solid #D6D0C2'
                }}
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-red-600 transition-colors"
                style={{ backgroundColor: '#51120A' }}
              >
                <Trash2 size={16} style={{ color: 'white' }} />
              </button>
            </div>
          ) : (
            <div
              onClick={() => imageFileInputRef.current?.click()}
              className="cursor-pointer border border-dashed rounded-lg p-8 text-center hover:bg-[#EEE7D8] transition-colors w-full"
              style={{ borderColor: '#D6D0C2' }}
            >
              {/* Mountain/landscape icon */}
              <svg 
                width="40" 
                height="40" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#51120A"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mx-auto mb-2"
                style={{ opacity: 0.6 }}
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '14px' }}>
                Click to upload photo
              </p>
              <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '12px', opacity: 0.7 }}>
                Min: 500x400px
              </p>
            </div>
          )}
          <input
            ref={imageFileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="title" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Title *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => {
            setFormData({ ...formData, title: e.target.value });
            setUserHasManuallyEdited(true);
          }}
          required
          data-testid="recipe-title-input"
          style={{ borderColor: '#D6D0C2', boxShadow: 'none', backgroundColor: 'transparent', fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}
          className="focus:ring-0 focus:border-[#D6D0C2]"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <Label htmlFor="category" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Categories *</Label>
          <button
            type="button"
            onClick={() => setShowManageCategories(true)}
            className="text-sm underline hover:opacity-70"
            style={{ color: '#875C66', fontFamily: "'Bricolage Grotesque', sans-serif" }}
          >
            Manage Categories
          </button>
        </div>
        
        {/* Category Multi-Select */}
        <div className="relative">
          {/* Selected tags display */}
          <div 
            className="flex flex-wrap items-center gap-2 p-2 rounded border min-h-[44px]"
            style={{ borderColor: '#D6D0C2', backgroundColor: 'transparent' }}
          >
            {formData.categories.map((cat, index) => (
              <div 
                key={index}
                className="flex items-center gap-1 px-2 py-1 rounded"
                style={{ backgroundColor: '#C9D7E9', color: '#0F2444', fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '14px' }}
              >
                {cat}
                <button
                  type="button"
                  onClick={() => {
                    const newCategories = formData.categories.filter((_, i) => i !== index);
                    setFormData({ ...formData, categories: newCategories });
                  }}
                  className="hover:opacity-70"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                    <path d="M6 4.586L9.293 1.293a1 1 0 011.414 1.414L7.414 6l3.293 3.293a1 1 0 01-1.414 1.414L6 7.414l-3.293 3.293a1 1 0 01-1.414-1.414L4.586 6 1.293 2.707a1 1 0 011.414-1.414L6 4.586z"/>
                  </svg>
                </button>
              </div>
            ))}
            <input
              type="text"
              placeholder={formData.categories.length === 0 ? "Type and press Enter to add categories..." : "Add more..."}
              value={categoryInput}
              onChange={(e) => setCategoryInput(e.target.value)}
              onFocus={() => setShowCategoryDropdown(true)}
              onBlur={() => setTimeout(() => setShowCategoryDropdown(false), 200)}
              className="flex-1 bg-transparent focus:outline-none min-w-[200px]"
              style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", border: 'none', outline: 'none' }}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (categoryInput.trim()) {
                    // Check if already selected
                    if (formData.categories.includes(categoryInput.trim())) {
                      toast.info("Category already added");
                      setCategoryInput('');
                      return;
                    }
                    // Check if category exists
                    const existingCat = localCategories.find(c => c.name.toLowerCase() === categoryInput.trim().toLowerCase());
                    if (existingCat) {
                      setFormData({ ...formData, categories: [...formData.categories, existingCat.name] });
                    } else {
                      // Create new category
                      try {
                        const response = await axios.post(
                          `${API}/categories`,
                          { name: categoryInput.trim() },
                          { headers: { Authorization: `Bearer ${adminToken}` } }
                        );
                        setFormData({ ...formData, categories: [...formData.categories, response.data.name] });
                        syncLocalCategories([...localCategories, response.data]);
                        toast.success('Category created and added!');
                      } catch (error) {
                        console.error('Failed to create category', error);
                        toast.error('Failed to create category');
                      }
                    }
                    setCategoryInput('');
                    setShowCategoryDropdown(false);
                  }
                }
              }}
            />
          </div>

          {/* Category List Dropdown */}
          {showCategoryDropdown && (
            <div 
              className="absolute z-10 w-full mt-1 rounded border max-h-60 overflow-y-auto"
              style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}
            >
              {localCategories
                .filter(cat => !formData.categories.includes(cat.name) && (categoryInput === '' || cat.name.toLowerCase().includes(categoryInput.toLowerCase())))
                .map((cat) => (
                <div
                  key={cat.id}
                  className="p-2 hover:bg-[#EEE7D8] cursor-pointer transition-colors"
                  onClick={() => {
                    setFormData({ ...formData, categories: [...formData.categories, cat.name] });
                    setCategoryInput('');
                    setShowCategoryDropdown(false);
                  }}
                >
                  <span style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '14px' }}>
                    {cat.name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <Label htmlFor="creator" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Creator</Label>
        <Input
          id="creator"
          value={formData.creator}
          onChange={(e) => setFormData({ ...formData, creator: e.target.value })}
          data-testid="recipe-creator-input"
          style={{ borderColor: '#D6D0C2', boxShadow: 'none', backgroundColor: 'transparent', fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}
          className="focus:ring-0 focus:border-[#D6D0C2]"
        />
      </div>

      <div>
        <Label htmlFor="source_link" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Source Link</Label>
        <Input
          id="source_link"
          value={formData.source_link}
          onChange={(e) => setFormData({ ...formData, source_link: e.target.value })}
          placeholder="https://..."
          data-testid="recipe-source-link-input"
          style={{ borderColor: '#D6D0C2', boxShadow: 'none', backgroundColor: 'transparent', fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}
          className="focus:ring-0 focus:border-[#D6D0C2]"
        />
      </div>

      <div className="relative">
        <Label htmlFor="ingredients" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Ingredients *</Label>
        <p className="text-xs mb-2" style={{ color: '#A0988A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
          Select text and tap a button to format as a list
        </p>
        
        {/* Static toolbar for mobile/tablet - Ingredients */}
        <div className="lg:hidden flex gap-1 p-1 rounded-lg mb-2 w-full" style={{ backgroundColor: '#EEE7D8' }}>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (ingredientsRef.current) {
                const textarea = ingredientsRef.current;
                let start = textarea.selectionStart;
                let end = textarea.selectionEnd;
                if (start === end) {
                  start = lastIngredientsSelection.current.start;
                  end = lastIngredientsSelection.current.end;
                }
                if (start !== end) {
                  formatIngredientsBulleted(start, end);
                } else {
                  toast.info("Select some text first");
                }
              }
            }}
            className="p-2 rounded transition-colors hover:bg-[#F3EEE4] active:bg-[#F3EEE4]"
            title="Bulleted List"
          >
            <List className="h-5 w-5" style={{ color: '#511209' }} />
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (ingredientsRef.current) {
                const textarea = ingredientsRef.current;
                let start = textarea.selectionStart;
                let end = textarea.selectionEnd;
                if (start === end) {
                  start = lastIngredientsSelection.current.start;
                  end = lastIngredientsSelection.current.end;
                }
                if (start !== end) {
                  formatIngredientsNumbered(start, end);
                } else {
                  toast.info("Select some text first");
                }
              }
            }}
            className="p-2 rounded transition-colors hover:bg-[#F3EEE4] active:bg-[#F3EEE4]"
            title="Numbered List"
          >
            <ListOrdered className="h-5 w-5" style={{ color: '#511209' }} />
          </button>
        </div>
        
        <Textarea
          ref={ingredientsRef}
          id="ingredients"
          onKeyDown={(e) => handleListEnter(e, "ingredients")}
          value={formData.ingredients}
          onChange={(e) => {
            setFormData({ ...formData, ingredients: e.target.value });
            setUserHasManuallyEdited(true);
          }}
          onSelect={handleIngredientsSelect}
          onMouseUp={handleIngredientsSelect}
          onTouchEnd={handleIngredientsSelect}
          onBlur={handleIngredientsBlur}
          rows={6}
          placeholder="Enter one per line"
          data-testid="recipe-ingredients-input"
          style={{ borderColor: '#D6D0C2', boxShadow: 'none', backgroundColor: 'transparent', fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}
          className="focus:ring-0 focus:border-[#D6D0C2]"
        />
        
        {/* Floating Format Toolbar - appears below selection (Desktop only) - Ingredients */}
        {showIngredientsToolbar && (
          <div 
            className="absolute z-50 hidden lg:flex gap-0.5 p-1.5 rounded-md shadow-lg transition-opacity duration-150"
            style={{ 
              top: `${ingredientsToolbarPosition.top}px`,
              left: `${ingredientsToolbarPosition.left}px`,
              backgroundColor: '#511209',
              transform: 'scale(0.9)'
            }}
            data-testid="ingredients-format-toolbar"
          >
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                formatIngredientsBulleted();
              }}
              className="p-1.5 rounded transition-colors hover-blush"
              title="Bulleted List"
            >
              <List className="h-5 w-5" style={{ color: '#F3EEE4' }} />
            </button>
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                formatIngredientsNumbered();
              }}
              className="p-1.5 rounded transition-colors hover-blush"
              title="Numbered List"
            >
              <ListOrdered className="h-5 w-5" style={{ color: '#F3EEE4' }} />
            </button>
          </div>
        )}
      </div>

      <div className="relative">
        <Label htmlFor="instructions" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Instructions *</Label>
        <p className="text-xs mb-2" style={{ color: '#A0988A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
          Select text and tap a button to format as a list
        </p>
        
        {/* Static toolbar for mobile/tablet */}
        <div className="lg:hidden flex gap-1 p-1 rounded-lg mb-2 w-full" style={{ backgroundColor: '#EEE7D8' }}>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (instructionsRef.current) {
                const textarea = instructionsRef.current;
                let start = textarea.selectionStart;
                let end = textarea.selectionEnd;
                if (start === end) {
                  start = lastInstructionsSelection.current.start;
                  end = lastInstructionsSelection.current.end;
                }
                if (start !== end) {
                  formatAsBulletedList(start, end);
                } else {
                  toast.info("Select some text first");
                }
              }
            }}
            className="p-2 rounded transition-colors hover:bg-[#F3EEE4] active:bg-[#F3EEE4]"
            title="Bulleted List"
          >
            <List className="h-5 w-5" style={{ color: '#511209' }} />
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (instructionsRef.current) {
                const textarea = instructionsRef.current;
                let start = textarea.selectionStart;
                let end = textarea.selectionEnd;
                if (start === end) {
                  start = lastInstructionsSelection.current.start;
                  end = lastInstructionsSelection.current.end;
                }
                if (start !== end) {
                  formatAsNumberedList(start, end);
                } else {
                  toast.info("Select some text first");
                }
              }
            }}
            className="p-2 rounded transition-colors hover:bg-[#F3EEE4] active:bg-[#F3EEE4]"
            title="Numbered List"
          >
            <ListOrdered className="h-5 w-5" style={{ color: '#511209' }} />
          </button>
        </div>
        
        <Textarea
          ref={instructionsRef}
          id="instructions"
          onKeyDown={(e) => handleListEnter(e, "instructions")}
          value={formData.instructions}
          onChange={(e) => {
            setFormData({ ...formData, instructions: e.target.value });
            setUserHasManuallyEdited(true);
          }}
          onSelect={handleInstructionsSelect}
          onMouseUp={handleInstructionsSelect}
          onTouchEnd={handleInstructionsSelect}
          onBlur={handleInstructionsBlur}
          rows={8}
          required
          data-testid="recipe-instructions-input"
          style={{ borderColor: '#D6D0C2', boxShadow: 'none', backgroundColor: 'transparent', fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}
          className="focus:ring-0 focus:border-[#D6D0C2]"
        />
        
        {/* Floating Format Toolbar - appears below selection (Desktop only) */}
        {showFormatToolbar && (
          <div 
            className="absolute z-50 hidden lg:flex gap-0.5 p-1.5 rounded-md shadow-lg transition-opacity duration-150"
            style={{ 
              top: `${toolbarPosition.top}px`,
              left: `${toolbarPosition.left}px`,
              backgroundColor: '#511209',
              transform: 'scale(0.9)'
            }}
            data-testid="format-toolbar"
          >
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                formatAsBulletedList();
              }}
              className="p-1.5 rounded transition-colors hover-blush"
              title="Bulleted List"
            >
              <List className="h-5 w-5" style={{ color: '#F3EEE4' }} />
            </button>
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                formatAsNumberedList();
              }}
              className="p-1.5 rounded transition-colors hover-blush"
              title="Numbered List"
            >
              <ListOrdered className="h-5 w-5" style={{ color: '#F3EEE4' }} />
            </button>
          </div>
        )}
      </div>

      {/* Notes Section */}
      <div>
        <Label htmlFor="notes" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Notes</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Add any personal notes about this recipe..."
          rows={4}
          data-testid="recipe-notes-input"
          style={{ borderColor: '#D6D0C2', boxShadow: 'none', backgroundColor: 'transparent', fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}
          className="focus:ring-0 focus:border-[#D6D0C2]"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label htmlFor="prep_time" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Prep Time</Label>
          <Input
            id="prep_time"
            value={formData.prep_time}
            onChange={(e) => setFormData({ ...formData, prep_time: e.target.value })}
            placeholder="15 mins"
            data-testid="recipe-prep-time-input"
            style={{ borderColor: '#D6D0C2', boxShadow: 'none', backgroundColor: 'transparent', fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}
            className="focus:ring-0 focus:border-[#D6D0C2]"
          />
        </div>
        <div>
          <Label htmlFor="cook_time" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Cook Time</Label>
          <Input
            id="cook_time"
            value={formData.cook_time}
            onChange={(e) => setFormData({ ...formData, cook_time: e.target.value })}
            placeholder="30 mins"
            data-testid="recipe-cook-time-input"
            style={{ borderColor: '#D6D0C2', boxShadow: 'none', backgroundColor: 'transparent', fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}
            className="focus:ring-0 focus:border-[#D6D0C2]"
          />
        </div>
        <div>
          <Label htmlFor="servings" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Servings</Label>
          <Input
            id="servings"
            type="number"
            inputMode="numeric"
            min="1"
            value={formData.servings}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9]/g, '');
              setFormData({ ...formData, servings: val });
            }}
            placeholder="4"
            data-testid="recipe-servings-input"
            style={{ borderColor: '#D6D0C2', boxShadow: 'none', backgroundColor: 'transparent', fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}
            className="focus:ring-0 focus:border-[#D6D0C2]"
          />
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel} 
          data-testid="cancel-recipe-btn"
          className="w-full sm:w-auto hover-blush transition-colors focus:outline-none focus:ring-0"
          style={{ borderWidth: '1px', borderColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          data-testid="save-recipe-btn"
          disabled={isProcessing || isSubmitting}
          className="w-full sm:w-auto focus:outline-none focus:ring-0"
          style={{ backgroundColor: '#51120A', borderColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", opacity: (isProcessing || isSubmitting) ? 0.5 : 1 }}
        >
          {isSubmitting ? "Saving..." : isProcessing ? "Processing..." : (submitLabel || (recipe ? "Update" : "Create") + " Recipe")}
        </Button>
      </div>

      {/* Manage Categories Dialog */}
      <Dialog open={showManageCategories} onOpenChange={setShowManageCategories}>
        <DialogContent className="sm:max-w-[500px]" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif", fontSize: '32px', lineHeight: 1.1 }}>
              Manage Categories
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Add New Category */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Add New Category
              </label>
              <div className="flex gap-2">
                <Input
                  value={newCategoryInput}
                  onChange={(e) => setNewCategoryInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateCategory();
                    }
                  }}
                  placeholder="Type and press Enter"
                  className="flex-1"
                  style={{ 
                    backgroundColor: '#EEE7D8',
                    borderColor: '#D6D0C2',
                    color: '#51120A',
                    fontFamily: "'Bricolage Grotesque', sans-serif"
                  }}
                />
              </div>
              <p className="text-sm mt-1" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", opacity: 0.7 }}>
                Press Enter to create a new category
              </p>
            </div>

            {/* Categories List with Drag and Drop */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Your Categories
              </label>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={localCategories.map(cat => cat.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {localCategories.length === 0 && (
                      <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", opacity: 0.5 }}>
                        No categories yet. Add your first category above.
                      </p>
                    )}
                    {localCategories.map((category) => (
                      <SortableCategoryItem 
                        key={category.id} 
                        category={category}
                        editingCategoryId={editingCategoryId}
                        editingCategoryName={editingCategoryName}
                        setEditingCategoryId={setEditingCategoryId}
                        setEditingCategoryName={setEditingCategoryName}
                        handleUpdateCategory={handleUpdateCategory}
                        handleDeleteCategory={handleDeleteCategory}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              onClick={() => setShowManageCategories(false)}
              style={{ backgroundColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation Dialog */}
      <Dialog open={deleteCategoryDialog.open} onOpenChange={(open) => !open && setDeleteCategoryDialog({ open: false, category: null, recipeCount: 0 })}>
        <DialogContent className="sm:max-w-[425px]" overlayClassName="bg-[#EEE7D8]/60 backdrop-blur-sm" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif", fontSize: '24px' }}>
              Delete Category
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", lineHeight: '1.6' }}>
              Are you sure you want to delete "{deleteCategoryDialog.category?.name}"?
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteCategoryDialog({ open: false, category: null, recipeCount: 0 })}
              style={{ 
                borderWidth: '1px',
                borderColor: '#51120A', 
                color: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif"
              }}
              className="hover-blush transition-colors"
            >
              No
            </Button>
            <Button
              onClick={confirmDeleteCategory}
              style={{ 
                backgroundColor: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif"
              }}
            >
              Yes, Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </fieldset>
    </form>
    </>
  );
};

export default RecipeForm;
